import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import PrivacyTrigger from '@alltrails/shared/components/PrivacyTrigger';
import classNames from 'classnames';

import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import OutlinedTextField from '../textFields/OutlinedTextField';
import SelectDropdown from '../SelectDropdown/SelectDropdown';
import DatePicker from '../../shared/DatePicker/DatePicker';
import StarRating from '../../shared/StarRating';
import { activities } from '../../../utils/activity_util';
import * as styles from './styles/styles.module.scss';
import { VISIBILITY_TO_OPTION } from '../../../utils/privacy_policy_helpers';
import useFormatMessage from 'hooks/useFormatMessage';

const TRACK_UPLOAD_FIELDS_STRINGS = defineMessages({
  RATING: { defaultMessage: 'Rating' },
  NAME: { defaultMessage: 'Name' },
  ACTIVITY_TITLE: { defaultMessage: 'Name (Optional)' },
  DESCRIPTION: { defaultMessage: 'Description' },
  ACTIVITY_DESCRIPTION: { defaultMessage: 'Description (Optional)' },
  TYPE: { defaultMessage: 'Type' },
  TRAIL: { defaultMessage: 'Trail' },
  DATE: { defaultMessage: 'Date' }
});

const TrackUploadFields = props => {
  const {
    formattedDefaultMessages: { RATING, NAME, ACTIVITY_TITLE, DESCRIPTION, ACTIVITY_DESCRIPTION, TYPE, TRAIL, DATE }
  } = useFormatMessage(TRACK_UPLOAD_FIELDS_STRINGS);
  const languageRegionCode = useLanguageRegionCode();

  const handleTextChange = e => {
    const { name, value } = e.currentTarget;
    const payload = { [name]: value };

    props.updateTrackForm(payload);
  };

  const handleActivityChange = e => {
    const value = e.currentTarget.dataset.value;
    const activity = activities.find(active => active.value === value);
    const payload = { activity };

    props.updateTrackForm(payload);
  };

  const handleNearbyTrailsChange = e => {
    const { value } = e.currentTarget.dataset;
    const payload = { trailId: parseInt(value) };

    props.updateTrackForm(payload);
  };

  const handleDateChange = date => {
    props.updateTrackForm({ date });
  };

  const handleRatingChange = (e, rating) => {
    e.preventDefault();
    props.updateTrackForm({ rating });
  };

  const togglePrivacy = e => {
    const isPrivate = e.currentTarget.checked;
    props.updateTrackForm({ isPrivate });
  };

  return (
    <div className={`${styles.formContainer} ${props.className}`}>
      <div className={`${styles.formSection} ${styles.alignedLabel} ${styles.spaceBetween}`}>
        <div className={styles.labelName}>{RATING}</div>
        <div className={styles.starsContainer}>
          <StarRating handler={handleRatingChange} config={props.rating} iconClassName={styles.starRatingIconClass} />
        </div>
      </div>
      <div className={`${styles.formSection} ${styles.alignedLabel} ${styles.spaceBetween}`}>
        <div className={styles.labelName}>{NAME}</div>
        <OutlinedTextField
          label={ACTIVITY_TITLE}
          value={props.name}
          name="name"
          changeHandler={handleTextChange}
          className={styles.nameAndDescription}
        />
      </div>
      <div className={`${styles.formSection} ${styles.alignedLabel} ${styles.spaceBetween}`}>
        <div className={styles.labelName}>{DESCRIPTION}</div>
        <OutlinedTextField
          label={ACTIVITY_DESCRIPTION}
          value={props.description}
          changeHandler={handleTextChange}
          name="description"
          multiline
          rows={2}
          className={styles.nameAndDescription}
        />
      </div>
      <div className={`${styles.formSection} ${styles.alignedLabel} ${styles.spaceBetween}`}>
        <div className={styles.labelName}>{TYPE}</div>
        <div className={styles.dropdownContainer}>
          <SelectDropdown
            options={activities}
            handleChange={handleActivityChange}
            defaultItem={props.activity}
            isMobile={props.isMobile}
            materialRootClass={classNames(styles.medium, { [styles.upload]: !props.editMode })}
          />
        </div>
      </div>
      {props.nearbyTrails.length > 0 && (
        <div className={`${styles.formSection} ${styles.alignedLabel} ${styles.spaceBetween}`}>
          <div className={styles.labelName}>{TRAIL}</div>
          <div className={styles.dropdownContainer}>
            <SelectDropdown
              className={styles.trailDropdown}
              defaultItem={props.nearbyTrails.find(nb => nb.value === props.trailId)}
              options={props.nearbyTrails}
              handleChange={handleNearbyTrailsChange}
              materialRootClass={classNames(styles.medium, { [styles.upload]: !props.editMode })}
            />
          </div>
        </div>
      )}
      <div className={`${styles.formSection} ${styles.alignedLabel} ${styles.spaceBetween}`}>
        <div className={styles.labelName}>{DATE}</div>
        <div className={styles.dropdownContainer}>
          <DatePicker date={props.date} onChange={handleDateChange} locale={languageRegionCode} materialRootClass={styles.datePicker} />
        </div>
      </div>
      <div className={`${styles.formSection} ${styles.spaceBetween} ${styles.privacyContainer}`}>
        <PrivacyTrigger
          onClick={props.handlePrivacyTriggerClick}
          label={<FormattedMessage defaultMessage="Privacy" />}
          currentOption={VISIBILITY_TO_OPTION[props.contentPrivacy]}
        />
      </div>
    </div>
  );
};

TrackUploadFields.propTypes = {
  updateTrackForm: PropTypes.func,
  name: PropTypes.string,
  description: PropTypes.string,
  activity: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  }),
  rating: PropTypes.number,
  className: PropTypes.string,
  nearbyTrails: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string
    })
  )
};

TrackUploadFields.defaultProps = {
  updateTrackForm: () => {},
  name: '',
  description: '',
  activity: {},
  rating: 0,
  nearbyTrails: [],
  className: ''
};

export default TrackUploadFields;
